import {
  ConcreteNotificationEventsQuery,
  ConcreteNotificationEventsQueryResult,
  useConcreteNotificationEventsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import { useProjectContext } from 'context/ProjectContext'

type Query = ConcreteNotificationEventsQuery
export type ConcreteNotification = Query['concreteNotifications']['records'][0]

type ConcreteNotificationsProviderContext = Pick<
  ConcreteNotificationEventsQueryResult,
  'refetch'
> & {
  data: {
    concreteNotifications: ConcreteNotificationEventsQuery['concreteNotifications']['records']
    concreteMixDesigns: ConcreteNotificationEventsQuery['concreteMixDesigns']['records']
    concreteMaterials: ConcreteNotificationEventsQuery['concreteMaterials']['records']
  }
  fetching: ConcreteNotificationEventsQueryResult['loading']
  recipients: UseRecipientsFn<ConcreteNotification['recipients']>
}

const { Provider, useContextFn } =
  createStrictContext<ConcreteNotificationsProviderContext>(
    'ConcreteNotificationsContext'
  )

export const useConcreteNotificationsContext = useContextFn

const ConcreteNotificationsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = useConcreteNotificationEventsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const recipients = useRecipientsFn({
    data,
    getter: (id, { concreteNotifications }) =>
      concreteNotifications.records.find(
        ({ notificationEventId }) => notificationEventId === id
      )?.recipients,
    fallback: {
      __typename: 'ConcreteNotificationRecipients',
      every: null,
      byConcreteMaterial: [],
      byConcreteMixDesign: [],
    },
  })
  return (
    <Provider
      value={{
        data: {
          concreteNotifications: data?.concreteNotifications.records ?? [],
          concreteMaterials: data?.concreteMaterials.records ?? [],
          concreteMixDesigns: data?.concreteMixDesigns.records ?? [],
        },
        fetching,
        refetch,
        recipients,
      }}
    >
      {children}
    </Provider>
  )
}

export default ConcreteNotificationsProvider
